import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
 breadcrumbListSchema,
 collectionPageSchema,
 faqPageSchema,
 jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { serviceFromSlug } from "@/lib/seo-services";
import { SEO_CITIES, citySlug, specialtySlug } from "@/lib/seo-slugs";
import { setSeoPageCache } from "@/lib/page-cache";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/services/$service")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const svc = serviceFromSlug(params.service);
 if (!svc) throw notFound();
 return { svc };
 },
 head: ({ params, loaderData }) => {
 if (!loaderData) return {};
 const { svc } = loaderData;
 const title = `${svc.name}, Find a Provider | Heallexa`;
 const description = `${svc.intro} Browse providers who offer ${svc.name.toLowerCase()} on Heallexa.`;
 const url = `${SITE}/services/${params.service}`;
 const decision = decideIndexability({
 providerCount: 1,
 hasFaq: true,
 hasNearbyCities: true,
 hasRelatedProviders: true,
 internalLinkCount: 30,
 });
 return {
 meta: [
 { title },
 { name: "description", content: description },
 robotsMeta(decision.robots),
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "article" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 jsonLdScript(collectionPageSchema({ name: title, description, url })),
 jsonLdScript(
 breadcrumbListSchema([
 { name: "Home", url: "/" },
 { name: "Services", url: "/services" },
 { name: svc.name, url: `/services/${svc.slug}` },
 ]),
 ),
 jsonLdScript(
 faqPageSchema([
 { q: `Who offers ${svc.name.toLowerCase()}?`, a: `${svc.specialties.join(", ")} typically provide ${svc.name.toLowerCase()}.` },
 { q: `Is ${svc.name.toLowerCase()} covered by insurance?`, a: `Most plans cover medically necessary services; cosmetic and elective options vary.` },
 { q: `Is Heallexa free?`, a: `Yes, searching and booking on Heallexa is free for patients.` },
 ]),
 ),
 ],
 };
 },
 component: ServicePage,
});

function ServicePage() {
 const { svc } = Route.useLoaderData();

 const specialtyLinks = svc.specialties.map((s: string) => ({
 label: `${s} offering ${svc.name.toLowerCase()}`,
 href: `/specialties/${specialtySlug(s)}`,
 }));

 const cityLinks = SEO_CITIES.slice(0, 24).map((c) => ({
 label: `${svc.name} in ${c}`,
 href: `/services/${svc.slug}/${citySlug(c)}`,
 }));

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Home", href: "/" },
 { name: "Services", href: "/services" },
 { name: svc.name },
 ]}
 />
 <header className="mb-8">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Service overview</p>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
 {svc.name}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">{svc.intro}</p>
 </header>

 <section className="mb-10 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
 Educational only, not medical advice. Always consult a qualified healthcare provider.
 </section>

 <Reveal from="up" delay={60}>
 <SeoLinkList heading="Who offers this service" links={specialtyLinks} columns={3} className="mb-10" />
 </Reveal>
 <Reveal from="up" delay={60}>
 <SeoLinkList heading={`${svc.name} by city`} links={cityLinks} columns={3} icon={MapPin} className="mb-10" />
 </Reveal>

 <SeoFaq
 className="mb-8"
 items={[
 { q: `How do I prepare for a ${svc.name.toLowerCase()} appointment?`, a: `Bring your insurance card, photo ID, list of medications, and any relevant records.` },
 { q: `How long does ${svc.name.toLowerCase()} usually take?`, a: `Duration varies by provider and case. Check the listing or call the office to confirm.` },
 { q: `Can I book online?`, a: `Many providers support online booking, look for the "Book online" button on the profile.` },
 ]}
 />

 <div className="text-sm text-muted-foreground">
 <Link to="/" className="text-primary hover:underline">Back to Heallexa</Link>
 </div>
 </main>
 <FooterSection />
 </div>
 );
}
